import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import {
  Briefcase,
  Plus,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Trash2,
  Edit,
  BarChart3,
  PieChart,
  Lightbulb,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Zap,
  Clock,
  Brain,
  Scale,
  ArrowRight,
  Wallet,
  Target,
  BookmarkCheck,
  ExternalLink,
  Search,
  DollarSign,
  Layers,
  CircleDot,
  Play,
  Square,
  LineChart,
  Wand2,
  Building2,
  Upload,
  Sparkles,
  PiggyBank
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useLoadingProgress, InlineLoader } from "@/components/LoadingIndicators";
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from "recharts";
import { FundDeepDive } from "@/components/FundDeepDive";
import { RebalanceWizard } from "@/components/RebalanceWizard";
import { AIInvestPlanDialog } from "@/components/AIInvestPlanDialog";
import { MonthlyTopUpDialog } from "@/components/MonthlyTopUpDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'];

const AGGRESSION_LEVELS = [
  { value: "conservative", label: "Conservative", description: "Lower risk, stable returns", color: "text-blue-400" },
  { value: "moderate", label: "Moderate", description: "Balanced risk and reward", color: "text-green-400" },
  { value: "moderate_aggressive", label: "Moderate Aggressive", description: "Higher growth potential", color: "text-amber-400" },
  { value: "aggressive", label: "Aggressive", description: "Maximum growth, higher volatility", color: "text-red-400" },
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value) => {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
};

export const MyPortfolios = () => {
  const { isPaperTrading } = useOutletContext();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newPortfolio, setNewPortfolio] = useState({ name: "", holdings: "" });
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [expandedPortfolio, setExpandedPortfolio] = useState(null);
  const [syncResults, setSyncResults] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoringXlsx, setRestoringXlsx] = useState(false);

  const handleRestoreBackup = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // reset picker so same file can be re-uploaded
    if (!file) return;
    if (!file.name.endsWith(".json")) {
      toast.error("Please upload the videra_wealth_backup.json file (not .xlsx)");
      return;
    }
    if (!window.confirm(`Restore backup from "${file.name}"?\n\nThis will MERGE the backup into your current data (safe - won't duplicate).`)) {
      return;
    }
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/admin/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const summary = Object.entries(data.result || {})
        .filter(([, v]) => (v.upserted ?? v.inserted ?? v.replaced ?? 0) > 0)
        .map(([k, v]) => `${k}: ${v.upserted ?? v.inserted ?? v.replaced ?? 0}`)
        .join(", ");
      toast.success(summary ? `Backup restored! ${summary}` : "Backup restored (no new data)");
      fetchPortfolios();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Restore failed";
      toast.error(typeof detail === "string" ? detail : "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const handleImportXlsx = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please upload an .xlsx file");
      return;
    }
    if (!window.confirm(`Import "${file.name}" into your portfolios?\n\nThis MERGES the spreadsheet data (upsert by portfolio name) - existing portfolios with the same name will be UPDATED, new ones will be added.`)) {
      return;
    }
    setRestoringXlsx(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/admin/import-xlsx`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const summary = Object.entries(data.result || {})
        .filter(([, v]) => (v.upserted ?? v.replaced ?? 0) > 0)
        .map(([k, v]) => `${k}: ${v.upserted ?? v.replaced ?? 0}`)
        .join(", ");
      toast.success(summary ? `Excel imported! ${summary}` : "Nothing to import from that file");
      fetchPortfolios();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Import failed";
      toast.error(typeof detail === "string" ? detail : "Import failed");
    } finally {
      setRestoringXlsx(false);
    }
  };
  const [syncStatus, setSyncStatus] = useState(null);
  
  // Rebalance state
  const [rebalanceDialogOpen, setRebalanceDialogOpen] = useState(false);
  const [selectedPortfolioForRebalance, setSelectedPortfolioForRebalance] = useState(null);
  const [rebalanceSuggestion, setRebalanceSuggestion] = useState(null);
  const [rebalanceLoading, setRebalanceLoading] = useState(false);
  const [selectedAggression, setSelectedAggression] = useState("moderate_aggressive");
  const [markingPending, setMarkingPending] = useState(false);
  
  // Fund Analysis state
  const [fundAnalysisDialogOpen, setFundAnalysisDialogOpen] = useState(false);
  const [fundAnalysis, setFundAnalysis] = useState(null);
  const [fundAnalysisLoading, setFundAnalysisLoading] = useState(false);
  const [selectedPortfolioForAnalysis, setSelectedPortfolioForAnalysis] = useState(null);
  
  // Simulation state
  const [activeSimulation, setActiveSimulation] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [startingSimulation, setStartingSimulation] = useState(false);
  const [endingSimulation, setEndingSimulation] = useState(false);
  
  // Fund Deep Dive state
  const [fundDeepDiveOpen, setFundDeepDiveOpen] = useState(false);
  const [selectedFundForDeepDive, setSelectedFundForDeepDive] = useState(null);
  
  // Rebalance Wizard state  
  const [rebalanceWizardOpen, setRebalanceWizardOpen] = useState(false);

  // AI Invest Plan state
  const [aiInvestPlanOpen, setAiInvestPlanOpen] = useState(false);
  const [selectedPortfolioForAiPlan, setSelectedPortfolioForAiPlan] = useState(null);
  const [selectedPortfolioForWizard, setSelectedPortfolioForWizard] = useState(null);

  // Monthly Top-Up state
  const [monthlyTopupOpen, setMonthlyTopupOpen] = useState(false);
  const [selectedPortfolioForTopup, setSelectedPortfolioForTopup] = useState(null);

  useEffect(() => {
    fetchPortfolios();
    fetchSyncStatus();
    return () => { rebalancePollCancelled.current = true; }; // stop polling if page unmounts
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/hl-portfolios/sync-status`);
      setSyncStatus(response.data);
    } catch (error) {
      console.error("Error fetching sync status:", error);
    }
  };

  const fetchSimulation = async (portfolioId) => {
    try {
      const response = await axios.get(`${API}/hl-portfolios/${portfolioId}/simulation`);
      if (response.data.has_simulation) {
        setActiveSimulation(response.data);
      } else {
        setActiveSimulation(null);
      }
    } catch (error) {
      console.error("Error fetching simulation:", error);
    }
  };

  const startSimulation = async () => {
    if (!selectedPortfolioForRebalance || !rebalanceSuggestion) return;
    
    setStartingSimulation(true);
    try {
      const response = await axios.post(
        `${API}/hl-portfolios/${selectedPortfolioForRebalance.id}/simulation/start`,
        { rebalance_suggestion: rebalanceSuggestion }
      );
      
      if (response.data.success) {
        toast.success("🎮 Simulation started! Track your rebalanced portfolio daily.");
        // Build summary from the simulation data for Day 0
        const sim = response.data.simulation;
        const latestDay = sim.daily_values?.[sim.daily_values.length - 1] || {};
        setActiveSimulation({ 
          has_simulation: true, 
          simulation: sim,
          summary: {
            days_running: 0,
            current_original_value: sim.original_value || latestDay.original_value || 0,
            current_rebalanced_value: sim.rebalanced_value || latestDay.rebalanced_value || 0,
            original_total_pnl: latestDay.original_pnl || 0,
            rebalanced_total_pnl: latestDay.rebalanced_pnl || 0,
            original_total_pnl_pct: latestDay.original_pnl_pct || 0,
            rebalanced_total_pnl_pct: latestDay.rebalanced_pnl_pct || 0,
            advantage: 0,
            advantage_pct: 0
          }
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start simulation");
    } finally {
      setStartingSimulation(false);
    }
  };

  const endSimulation = async () => {
    if (!selectedPortfolioForRebalance) return;
    
    setEndingSimulation(true);
    try {
      const response = await axios.post(
        `${API}/hl-portfolios/${selectedPortfolioForRebalance.id}/simulation/end`
      );
      
      if (response.data.success) {
        const results = response.data.final_results;
        const wasWorth = results.comparison.rebalancing_was_better;
        toast.success(
          wasWorth 
            ? `✅ Rebalancing would have earned you £${results.comparison.pnl_difference.toFixed(2)} more!` 
            : `📊 Original portfolio performed better by £${Math.abs(results.comparison.pnl_difference).toFixed(2)}`
        );
        setActiveSimulation(null);
      }
    } catch (error) {
      toast.error("Failed to end simulation");
    } finally {
      setEndingSimulation(false);
    }
  };

  const fetchPortfolios = async () => {
    try {
      const response = await axios.get(`${API}/hl-portfolios`);
      setPortfolios(response.data.portfolios || []);
    } catch (error) {
      console.error("Error fetching portfolios:", error);
    } finally {
      setLoading(false);
    }
  };

  const syncPrices = async (portfolioId) => {
    setSyncing(portfolioId);
    try {
      const response = await axios.post(`${API}/hl-portfolios/${portfolioId}/sync-prices`);
      setSyncResults(response.data);
      toast.success(`Synced ${response.data.synced_count} holdings with live prices`);
      fetchPortfolios();
    } catch (error) {
      toast.error("Failed to sync prices");
    } finally {
      setSyncing(null);
    }
  };

  const syncAllPrices = async () => {
    setSyncingAll(true);
    try {
      const response = await axios.post(`${API}/hl-portfolios/sync-all`);
      toast.success(`Synced prices for ${response.data.results.length} portfolios`);
      fetchPortfolios();
      fetchSyncStatus();
    } catch (error) {
      toast.error("Failed to sync all portfolios");
    } finally {
      setSyncingAll(false);
    }
  };

  const addPortfolio = async () => {
    if (!newPortfolio.name || !newPortfolio.holdings) {
      toast.error("Please enter portfolio name and holdings");
      return;
    }

    try {
      const response = await axios.post(`${API}/hl-portfolios`, {
        name: newPortfolio.name,
        holdings_text: newPortfolio.holdings
      });
      toast.success("Portfolio added successfully");
      setAddDialogOpen(false);
      setNewPortfolio({ name: "", holdings: "" });
      fetchPortfolios();
    } catch (error) {
      toast.error("Failed to add portfolio");
    }
  };

  const deletePortfolio = async (portfolioId) => {
    if (!window.confirm("Are you sure you want to delete this portfolio?")) return;
    
    try {
      await axios.delete(`${API}/hl-portfolios/${portfolioId}`);
      toast.success("Portfolio deleted");
      fetchPortfolios();
    } catch (error) {
      toast.error("Failed to delete portfolio");
    }
  };

  const analyzePortfolio = async (portfolioId) => {
    setAnalyzing(portfolioId);
    try {
      const response = await axios.post(`${API}/hl-portfolios/${portfolioId}/analyze`);
      setSelectedAnalysis(response.data);
      toast.success("Analysis complete");
      fetchPortfolios();
    } catch (error) {
      toast.error("Failed to analyze portfolio");
    } finally {
      setAnalyzing(null);
    }
  };

  const getReturnColor = (value) => {
    if (value >= 20) return "text-success";
    if (value >= 0) return "text-green-400";
    if (value >= -10) return "text-warning";
    return "text-destructive";
  };

  const getRiskBadge = (risk) => {
    const colors = {
      "Low": "bg-success/20 text-success",
      "Medium": "bg-warning/20 text-warning",
      "High": "bg-destructive/20 text-destructive",
    };
    return colors[risk] || "bg-muted text-muted-foreground";
  };

  // Fund Analysis Functions
  const openFundAnalysisDialog = async (portfolio) => {
    setSelectedPortfolioForAnalysis(portfolio);
    setFundAnalysis(null);
    setFundAnalysisDialogOpen(true);
    setFundAnalysisLoading(true);
    
    try {
      const response = await axios.post(`${API}/hl-portfolios/${portfolio.id}/fund-analysis`);
      setFundAnalysis(response.data);
    } catch (error) {
      toast.error("Failed to analyze portfolio funds");
      console.error(error);
    } finally {
      setFundAnalysisLoading(false);
    }
  };

  // Rebalance Functions
  const rebalancePollCancelled = useRef(false);

  const openRebalanceDialog = async (portfolio) => {
    rebalancePollCancelled.current = false;
    setSelectedPortfolioForRebalance(portfolio);
    setRebalanceSuggestion(null);
    setRebalanceDialogOpen(true);
    // Fetch active simulation if exists
    await fetchSimulation(portfolio.id);
  };

  const fetchRebalanceSuggestion = async () => {
    if (!selectedPortfolioForRebalance) return;

    setRebalanceLoading(true);
    try {
      const { data: job } = await axios.post(
        `${API}/hl-portfolios/${selectedPortfolioForRebalance.id}/rebalance-suggestion`,
        { aggression: selectedAggression }
      );
      // Analysis runs as a background job (3-agent AI debate can take 45-60s+) - poll for it
      const jobId = job.job_id;
      const maxAttempts = 40; // ~2 minutes at 3s intervals
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((r) => setTimeout(r, 3000));
        if (rebalancePollCancelled.current) return; // dialog closed / unmounted mid-poll
        const { data: status } = await axios.get(`${API}/rebalance-jobs/${jobId}`);
        if (rebalancePollCancelled.current) return;
        if (status.status === "COMPLETE") {
          setRebalanceSuggestion(status.result);
          toast.success("Rebalance suggestion generated");
          return;
        }
        if (status.status === "ERROR") {
          toast.error(status.error || "Failed to generate rebalance suggestion");
          return;
        }
      }
      if (!rebalancePollCancelled.current) {
        toast.error("Analysis is taking longer than expected. Please try again.");
      }
    } catch (error) {
      if (!rebalancePollCancelled.current) {
        toast.error(error.response?.data?.detail || "Failed to generate rebalance suggestion");
        console.error(error);
      }
    } finally {
      if (!rebalancePollCancelled.current) setRebalanceLoading(false);
    }
  };

  const markAsPendingRebalance = async () => {
    if (!rebalanceSuggestion) return;
    
    setMarkingPending(true);
    try {
      await axios.post(
        `${API}/hl-portfolios/${selectedPortfolioForRebalance.id}/mark-pending-rebalance`,
        rebalanceSuggestion.suggested_changes
      );
      toast.success("Portfolio marked for rebalancing");
      setRebalanceDialogOpen(false);
      fetchPortfolios();
    } catch (error) {
      toast.error("Failed to mark as pending");
    } finally {
      setMarkingPending(false);
    }
  };

  // Prepare chart data for current allocation
  const getCurrentAllocationChartData = () => {
    if (!rebalanceSuggestion?.current_allocation) return [];
    return rebalanceSuggestion.current_allocation.map((item, idx) => ({
      name: item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name,
      value: item.weight,
      fullName: item.name
    }));
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="portfolios-loading">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="my-portfolios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            My HL Portfolios
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Import and analyze your Hargreaves Lansdown portfolios
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2 flex-wrap">
            <input
              id="restore-backup-input"
              type="file"
              accept=".json,application/json"
              onChange={handleRestoreBackup}
              style={{ display: "none" }}
              data-testid="restore-backup-input"
            />
            <input
              id="import-xlsx-input"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleImportXlsx}
              style={{ display: "none" }}
              data-testid="import-xlsx-input"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById("import-xlsx-input")?.click()}
              disabled={restoringXlsx}
              data-testid="import-xlsx-btn"
              title="Upload videra_wealth_backup.xlsx to sync portfolios, paper portfolio, challenge and trades"
            >
              {restoringXlsx ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Import Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => document.getElementById("restore-backup-input")?.click()}
              disabled={restoring}
              data-testid="restore-backup-btn"
              title="Upload videra_wealth_backup.json to restore all portfolios, settings, and challenge data"
            >
              {restoring ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Restore Backup
            </Button>
            <Button 
              variant="outline"
              onClick={syncAllPrices}
              disabled={syncingAll}
              data-testid="sync-all-btn"
            >
              {syncingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Sync All Prices
            </Button>
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-portfolio-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Import Portfolio
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Import HL Portfolio</DialogTitle>
              <DialogDescription>
                Copy and paste your holdings from Hargreaves Lansdown. Include fund name, value, and gain/loss.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Portfolio Name</Label>
                <Input
                  placeholder="e.g., AI Fund, Dad Portfolio"
                  value={newPortfolio.name}
                  onChange={(e) => setNewPortfolio({ ...newPortfolio, name: e.target.value })}
                  data-testid="portfolio-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Holdings (paste from HL)</Label>
                <Textarea
                  placeholder={`Paste your holdings here, e.g.:
Fundsmith Equity £707.59 -£9.80 (-1.37%)
Jupiter UK Income £1,436.43 +£393.20 (+37.69%)
LLOY £102.45 +£47.67 (+87.02%)`}
                  value={newPortfolio.holdings}
                  onChange={(e) => setNewPortfolio({ ...newPortfolio, holdings: e.target.value })}
                  className="min-h-[200px] font-mono text-sm"
                  data-testid="portfolio-holdings-input"
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={addPortfolio} data-testid="save-portfolio-btn">
                Import Portfolio
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </div>
          {/* Last Sync Status */}
          {syncStatus && (
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Clock className="w-3 h-3" />
              <span>
                Auto-sync: Daily at 5:30 PM UK • Next in {syncStatus.next_sync_in_hours}h
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Portfolio Summary Cards */}
      {portfolios.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12">
            <div className="text-center">
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-medium mb-2">No Portfolios Yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Import your Hargreaves Lansdown portfolios to get AI-powered analysis and recommendations.
              </p>
              <Button onClick={() => setAddDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Import Your First Portfolio
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {portfolios.map((portfolio) => (
            <Card key={portfolio.id} className="card-hover" data-testid={`portfolio-${portfolio.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-6 h-6 text-primary" />
                    <div>
                      <CardTitle className="text-lg">{portfolio.name}</CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        <span>{portfolio.holdings?.length || 0} holdings</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Prices: {portfolio.last_price_sync 
                            ? new Date(portfolio.last_price_sync).toLocaleString('en-GB', { 
                                day: '2-digit', 
                                month: 'short', 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })
                            : 'Never synced'}
                        </span>
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => analyzePortfolio(portfolio.id)}
                      disabled={analyzing === portfolio.id}
                      data-testid={`analyze-${portfolio.id}`}
                    >
                      {analyzing === portfolio.id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Lightbulb className="w-4 h-4 mr-2" />
                      )}
                      {analyzing === portfolio.id ? "Analyzing..." : "Get AI Advice"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => syncPrices(portfolio.id)}
                      disabled={syncing === portfolio.id}
                      data-testid={`sync-${portfolio.id}`}
                    >
                      {syncing === portfolio.id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      Sync Prices
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                      onClick={() => openFundAnalysisDialog(portfolio)}
                      data-testid={`fund-analysis-${portfolio.id}`}
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Fund Analysis
                    </Button>
                    <Button
                      size="sm"
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                      onClick={() => openRebalanceDialog(portfolio)}
                      data-testid={`rebalance-${portfolio.id}`}
                    >
                      <Scale className="w-4 h-4 mr-2" />
                      Rebalance
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-green-500/50 text-green-500 hover:bg-green-500/10"
                      onClick={() => {
                        setSelectedPortfolioForWizard(portfolio);
                        setRebalanceWizardOpen(true);
                      }}
                      data-testid={`wizard-${portfolio.id}`}
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      AI Wizard
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      onClick={() => {
                        setSelectedPortfolioForAiPlan(portfolio);
                        setAiInvestPlanOpen(true);
                      }}
                      data-testid={`ai-invest-plan-${portfolio.id}`}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      AI Invest Plan
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                      onClick={() => {
                        setSelectedPortfolioForTopup(portfolio);
                        setMonthlyTopupOpen(true);
                      }}
                      data-testid={`monthly-topup-${portfolio.id}`}
                    >
                      <PiggyBank className="w-4 h-4 mr-2" />
                      Add This Month's £{portfolio.monthly_topup_amount || 250}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deletePortfolio(portfolio.id)}
                      data-testid={`delete-${portfolio.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Syncing Progress Indicator */}
                {syncing === portfolio.id && (
                  <div className="mb-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">Syncing Live Prices...</p>
                        <p className="text-xs text-muted-foreground">Fetching latest market data from exchanges</p>
                      </div>
                    </div>
                    <Progress className="h-1 mt-2" />
                  </div>
                )}
                
                {/* Analyzing Progress Indicator */}
                {analyzing === portfolio.id && (
                  <div className="mb-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <div className="flex items-center gap-3">
                      <Brain className="w-5 h-5 animate-pulse text-purple-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">AI Analyzing Portfolio...</p>
                        <p className="text-xs text-muted-foreground">Evaluating risk, diversification, and opportunities</p>
                      </div>
                    </div>
                    <Progress className="h-1 mt-2" />
                  </div>
                )}
                
                {/* Last Synced */}
                {portfolio.last_price_sync && (
                  <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    Last synced: {new Date(portfolio.last_price_sync).toLocaleString()}
                  </div>
                )}
                {/* Portfolio Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="p-3 bg-muted/30 rounded-md">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Value</p>
                    <p className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {formatCurrency(portfolio.total_value || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-md">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Gain/Loss</p>
                    <p className={`text-xl font-bold ${getReturnColor(portfolio.total_return_pct || 0)}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {formatPercent(portfolio.total_return_pct || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-md">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Best Performer</p>
                    <p className="text-sm font-medium truncate">{portfolio.best_performer || "-"}</p>
                    <p className="text-xs text-success">{portfolio.best_return ? formatPercent(portfolio.best_return) : "-"}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-md">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Worst Performer</p>
                    <p className="text-sm font-medium truncate">{portfolio.worst_performer || "-"}</p>
                    <p className="text-xs text-destructive">{portfolio.worst_return ? formatPercent(portfolio.worst_return) : "-"}</p>
                  </div>
                </div>

                {/* Expandable Holdings */}
                <Accordion type="single" collapsible>
                  <AccordionItem value="holdings" className="border-none">
                    <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline">
                      View All Holdings ({portfolio.holdings?.length || 0})
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 mt-2">
                        {portfolio.holdings?.map((holding, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2 rounded-md bg-muted/20 hover:bg-muted/30 cursor-pointer group"
                            onClick={() => {
                              setSelectedFundForDeepDive(holding.name);
                              setFundDeepDiveOpen(true);
                            }}
                            data-testid={`holding-${idx}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">{holding.name}</p>
                                <Building2 className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-muted-foreground">{holding.type || "Fund"}</p>
                                {holding.proxy_symbol && holding.tracked !== false && (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                                    {holding.proxy_symbol}
                                  </Badge>
                                )}
                                {holding.tracked === false && (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] px-1 py-0 border-amber-500/40 text-amber-500"
                                    data-testid={`holding-untracked-badge-${idx}`}
                                  >
                                    Not tracked - update manually
                                  </Badge>
                                )}
                              </div>
                              {holding.last_synced && (
                                <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                                  Synced {new Date(holding.last_synced).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium tabular-nums">{formatCurrency(holding.value)}</p>
                              <p className={`text-xs ${getReturnColor(holding.return_pct)}`}>
                                {formatPercent(holding.return_pct)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {/* AI Analysis (if available) */}
                {portfolio.analysis && (
                  <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-md">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-5 h-5 text-primary" />
                      <h4 className="font-medium">AI Analysis</h4>
                      <Badge className={getRiskBadge(portfolio.analysis.risk_level)}>
                        {portfolio.analysis.risk_level} Risk
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {portfolio.analysis.summary}
                    </p>
                    
                    {portfolio.analysis.recommendations && portfolio.analysis.recommendations.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Recommendations:</p>
                        {portfolio.analysis.recommendations.map((rec, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <Badge variant="outline" className={
                              rec.action === "BUY" ? "text-success border-success/30" :
                              rec.action === "SELL" ? "text-destructive border-destructive/30" :
                              "text-warning border-warning/30"
                            }>
                              {rec.action}
                            </Badge>
                            <span>{rec.reason}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {portfolio.analysis.diversification_score && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Diversification Score</span>
                          <span>{portfolio.analysis.diversification_score}/100</span>
                        </div>
                        <Progress value={portfolio.analysis.diversification_score} className="h-1.5" />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cross-Portfolio Analysis */}
      {portfolios.length > 1 && (
        <Card data-testid="cross-portfolio-analysis">
          <CardHeader>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Portfolio Comparison
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {portfolios
                .sort((a, b) => (b.total_return_pct || 0) - (a.total_return_pct || 0))
                .map((portfolio, idx) => (
                  <div key={portfolio.id} className="flex items-center gap-4">
                    <div className="w-6 text-center">
                      <span className={`text-sm font-bold ${idx === 0 ? 'text-success' : 'text-muted-foreground'}`}>
                        #{idx + 1}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{portfolio.name}</span>
                        <span className={`text-sm font-bold ${getReturnColor(portfolio.total_return_pct || 0)}`}>
                          {formatPercent(portfolio.total_return_pct || 0)}
                        </span>
                      </div>
                      <Progress 
                        value={Math.min(100, Math.max(0, (portfolio.total_return_pct || 0) + 20))} 
                        className="h-2"
                      />
                    </div>
                    <div className="w-24 text-right">
                      <span className="text-sm text-muted-foreground">
                        {formatCurrency(portfolio.total_value || 0)}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rebalance Dialog */}
      <Dialog
        open={rebalanceDialogOpen}
        onOpenChange={(open) => {
          if (!open) rebalancePollCancelled.current = true; // stop polling once dialog closes
          setRebalanceDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-amber-500" />
              Rebalance Portfolio
            </DialogTitle>
            <DialogDescription>
              {selectedPortfolioForRebalance?.name} • {formatCurrency(selectedPortfolioForRebalance?.total_value || 0)}
            </DialogDescription>
          </DialogHeader>

          {/* Risk Appetite Selector */}
          <div className="space-y-3 py-4 border-b">
            <Label className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Select Your Risk Appetite
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {AGGRESSION_LEVELS.map((level) => (
                <Button
                  key={level.value}
                  variant={selectedAggression === level.value ? "default" : "outline"}
                  className={`flex flex-col h-auto py-3 ${selectedAggression === level.value ? 'ring-2 ring-amber-500' : ''}`}
                  onClick={() => setSelectedAggression(level.value)}
                  data-testid={`aggression-${level.value}`}
                >
                  <span className={`text-sm font-medium ${selectedAggression === level.value ? '' : level.color}`}>
                    {level.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-1">{level.description}</span>
                </Button>
              ))}
            </div>
            <Button 
              onClick={fetchRebalanceSuggestion} 
              disabled={rebalanceLoading}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              data-testid="generate-suggestion-btn"
            >
              {rebalanceLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing Portfolio...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  Generate Rebalance Suggestion
                </>
              )}
            </Button>
          </div>

          {/* Rebalance Results */}
          {rebalanceSuggestion && (
            <div className="space-y-6 py-4">
              {/* Summary */}
              <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg border border-amber-500/20">
                <p className="text-sm">{rebalanceSuggestion.summary}</p>
              </div>

              {/* Tabs for different views */}
              <Tabs defaultValue="macro" className="w-full">
                <TabsList className="grid w-full grid-cols-7">
                  <TabsTrigger value="macro">🌍 Macro</TabsTrigger>
                  <TabsTrigger value="diversification">📊 Score</TabsTrigger>
                  <TabsTrigger value="debate">🎯 AI</TabsTrigger>
                  <TabsTrigger value="changes">📝 Trades</TabsTrigger>
                  <TabsTrigger value="simulation">📈 5-Year</TabsTrigger>
                  <TabsTrigger value="basket">🛒 HL Picks</TabsTrigger>
                  <TabsTrigger value="runsim">🎮 Simulate</TabsTrigger>
                </TabsList>

                {/* Macro-Economic Factors Tab - NEW */}
                <TabsContent value="macro" className="space-y-4">
                  {rebalanceSuggestion.macro_factors && (
                    <div className="space-y-4">
                      {/* Overall Risk & Sentiment */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className={`border-2 ${
                          rebalanceSuggestion.macro_factors.overall_risk_level === 'LOW' ? 'border-green-500/50' :
                          rebalanceSuggestion.macro_factors.overall_risk_level === 'MEDIUM' ? 'border-amber-500/50' :
                          rebalanceSuggestion.macro_factors.overall_risk_level === 'HIGH' ? 'border-red-500/50' :
                          'border-red-700/50'
                        }`}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Overall Risk Level</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Badge className={`text-lg ${
                              rebalanceSuggestion.macro_factors.overall_risk_level === 'LOW' ? 'bg-green-500' :
                              rebalanceSuggestion.macro_factors.overall_risk_level === 'MEDIUM' ? 'bg-amber-500' :
                              rebalanceSuggestion.macro_factors.overall_risk_level === 'HIGH' ? 'bg-red-500' :
                              'bg-red-700'
                            }`}>
                              {rebalanceSuggestion.macro_factors.overall_risk_level}
                            </Badge>
                          </CardContent>
                        </Card>

                        <Card className={`border-2 ${
                          rebalanceSuggestion.macro_factors.market_sentiment === 'BULLISH' ? 'border-green-500/50' :
                          rebalanceSuggestion.macro_factors.market_sentiment === 'BEARISH' ? 'border-red-500/50' :
                          'border-amber-500/50'
                        }`}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Market Sentiment</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Badge className={`text-lg ${
                              rebalanceSuggestion.macro_factors.market_sentiment === 'BULLISH' ? 'bg-green-500' :
                              rebalanceSuggestion.macro_factors.market_sentiment === 'BEARISH' ? 'bg-red-500' :
                              'bg-amber-500'
                            }`}>
                              {rebalanceSuggestion.macro_factors.market_sentiment}
                            </Badge>
                          </CardContent>
                        </Card>

                        <Card className="border-primary/50">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Recommended Action</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <Badge className="text-lg bg-primary">
                              {rebalanceSuggestion.macro_factors.recommended_action}
                            </Badge>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Economic Factors */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            📉 Economic Indicators
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {rebalanceSuggestion.macro_factors.economic_factors && Object.entries(rebalanceSuggestion.macro_factors.economic_factors).map(([key, data]) => (
                              <div key={key} className="p-3 bg-muted/30 rounded-lg">
                                <p className="text-xs text-muted-foreground capitalize mb-1">{key.replace('_', ' ')}</p>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className={`text-xs ${
                                    data?.impact === 'POSITIVE' ? 'border-green-500 text-green-500' :
                                    data?.impact === 'NEGATIVE' ? 'border-red-500 text-red-500' :
                                    'border-amber-500 text-amber-500'
                                  }`}>
                                    {data?.status || 'N/A'}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{data?.detail || ''}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Monetary Policy */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            🏛️ Central Bank Policy
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {rebalanceSuggestion.macro_factors.monetary_policy && Object.entries(rebalanceSuggestion.macro_factors.monetary_policy).map(([bank, data]) => (
                              <div key={bank} className="p-3 bg-muted/30 rounded-lg">
                                <p className="font-medium text-sm mb-2">{bank.toUpperCase()}</p>
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Stance</span>
                                    <Badge variant="outline" className={`text-xs ${
                                      data?.stance === 'HAWKISH' ? 'border-red-500 text-red-500' :
                                      data?.stance === 'DOVISH' ? 'border-green-500 text-green-500' :
                                      'border-amber-500 text-amber-500'
                                    }`}>
                                      {data?.stance || 'N/A'}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Next Move</span>
                                    <span className="font-medium">{data?.next_move || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Geopolitical Risks */}
                      {rebalanceSuggestion.macro_factors.geopolitical_risks?.length > 0 && (
                        <Card className="border-red-500/30">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              ⚠️ Geopolitical Risks
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {rebalanceSuggestion.macro_factors.geopolitical_risks.map((risk, idx) => (
                                <div key={idx} className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-sm">{risk.region}</span>
                                    <Badge className={`${
                                      risk.severity === 'HIGH' ? 'bg-red-500' :
                                      risk.severity === 'MEDIUM' ? 'bg-amber-500' :
                                      'bg-green-500'
                                    }`}>
                                      {risk.severity} - {risk.risk_type?.replace('_', ' ')}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground mb-2">
                                    Sectors impacted: {risk.sectors_impacted?.join(', ') || 'N/A'}
                                  </p>
                                  <p className="text-xs font-medium text-primary">
                                    Action: {risk.investor_action}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Sector Outlook */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Favored Sectors */}
                        <Card className="border-green-500/30">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2 text-green-500">
                              ✓ Favored Sectors
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {rebalanceSuggestion.macro_factors.sector_outlook?.favored?.map((sector, idx) => (
                                <div key={idx} className="p-2 bg-green-500/5 rounded">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">{sector.sector}</span>
                                    <Badge variant="outline" className="text-green-500 border-green-500">
                                      {sector.recommended_allocation}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground">{sector.reason}</p>
                                </div>
                              ))}
                              {(!rebalanceSuggestion.macro_factors.sector_outlook?.favored || 
                                rebalanceSuggestion.macro_factors.sector_outlook.favored.length === 0) && (
                                <p className="text-xs text-muted-foreground">No sectors currently favored</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>

                        {/* Sectors to Avoid */}
                        <Card className="border-red-500/30">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2 text-red-500">
                              ✗ Sectors to Avoid
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {rebalanceSuggestion.macro_factors.sector_outlook?.avoid?.map((sector, idx) => (
                                <div key={idx} className="p-2 bg-red-500/5 rounded">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">{sector.sector}</span>
                                    <Badge variant="outline" className="text-red-500 border-red-500">
                                      {sector.recommended_allocation}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground">{sector.reason}</p>
                                </div>
                              ))}
                              {(!rebalanceSuggestion.macro_factors.sector_outlook?.avoid || 
                                rebalanceSuggestion.macro_factors.sector_outlook.avoid.length === 0) && (
                                <p className="text-xs text-muted-foreground">No sectors to avoid currently</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Upcoming Events */}
                      {rebalanceSuggestion.macro_factors.upcoming_events?.length > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              📅 Upcoming Events to Watch
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {rebalanceSuggestion.macro_factors.upcoming_events.map((event, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                                  <div>
                                    <p className="font-medium text-sm">{event.event}</p>
                                    <p className="text-xs text-muted-foreground">{event.date}</p>
                                  </div>
                                  <div className="text-right">
                                    <Badge className={`${
                                      event.potential_impact === 'HIGH' ? 'bg-red-500' :
                                      event.potential_impact === 'MEDIUM' ? 'bg-amber-500' :
                                      'bg-green-500'
                                    }`}>
                                      {event.potential_impact} Impact
                                    </Badge>
                                    <p className="text-xs text-muted-foreground mt-1">{event.what_to_watch}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Portfolio Recommendation */}
                      <Card className="bg-primary/5 border-primary/30">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            💡 Recommendation for Your Risk Profile
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm">
                            {rebalanceSuggestion.macro_factors.portfolio_recommendations?.[selectedAggression] || 
                             rebalanceSuggestion.macro_factors.portfolio_recommendations?.moderate ||
                             'Focus on quality and diversification.'}
                          </p>
                        </CardContent>
                      </Card>

                      {/* Summary */}
                      <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
                        <h4 className="font-semibold text-sm mb-2">Executive Summary</h4>
                        <p className="text-sm text-muted-foreground">
                          {rebalanceSuggestion.macro_factors.summary}
                        </p>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Diversification Score Tab - NEW */}
                <TabsContent value="diversification" className="space-y-4">
                  {rebalanceSuggestion.diversification && (
                    <div className="space-y-4">
                      {/* Score Overview */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className={`border-2 ${
                          rebalanceSuggestion.diversification.score >= 80 ? 'border-green-500/50' :
                          rebalanceSuggestion.diversification.score >= 65 ? 'border-blue-500/50' :
                          rebalanceSuggestion.diversification.score >= 50 ? 'border-amber-500/50' :
                          'border-red-500/50'
                        }`}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Your Diversification Score</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-center gap-4">
                              <div className="relative w-24 h-24">
                                <svg className="w-24 h-24 -rotate-90">
                                  <circle cx="48" cy="48" r="40" fill="none" stroke="#334155" strokeWidth="8" />
                                  <circle 
                                    cx="48" cy="48" r="40" fill="none" 
                                    stroke={
                                      rebalanceSuggestion.diversification.score >= 80 ? '#22c55e' :
                                      rebalanceSuggestion.diversification.score >= 65 ? '#3b82f6' :
                                      rebalanceSuggestion.diversification.score >= 50 ? '#f59e0b' : '#ef4444'
                                    }
                                    strokeWidth="8"
                                    strokeDasharray={`${rebalanceSuggestion.diversification.score * 2.51} 251`}
                                    strokeLinecap="round"
                                  />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-2xl font-bold">{rebalanceSuggestion.diversification.score}</span>
                                </div>
                              </div>
                              <div>
                                <Badge className={`mb-2 ${
                                  rebalanceSuggestion.diversification.rating === 'excellent' ? 'bg-green-500' :
                                  rebalanceSuggestion.diversification.rating === 'good' ? 'bg-blue-500' :
                                  rebalanceSuggestion.diversification.rating === 'fair' ? 'bg-amber-500' :
                                  'bg-red-500'
                                }`}>
                                  {rebalanceSuggestion.diversification.rating?.toUpperCase()}
                                </Badge>
                                <p className="text-xs text-muted-foreground">
                                  {rebalanceSuggestion.diversification.rating_description}
                                </p>
                                <p className="text-xs mt-2">
                                  <span className="text-primary font-medium">Target: 80/100</span>
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Score Breakdown */}
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Score Breakdown</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {Object.entries(rebalanceSuggestion.diversification.breakdown || {}).map(([key, data]) => (
                              <div key={key} className="space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span className="capitalize">{key.replace('_', ' ')}</span>
                                  <span>{data.score}/{data.max}</span>
                                </div>
                                <Progress value={(data.score / data.max) * 100} className="h-2" />
                                <p className="text-[10px] text-muted-foreground">{data.detail}</p>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      </div>

                      {/* Why Diversify */}
                      <Card className="bg-primary/5">
                        <CardContent className="py-4">
                          <h4 className="font-semibold text-sm mb-2">Why Diversification Matters</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            <div className="flex items-start gap-2"><span>✓</span> Reduces risk of any single investment hurting you</div>
                            <div className="flex items-start gap-2"><span>✓</span> Smooths out returns over time (less volatility)</div>
                            <div className="flex items-start gap-2"><span>✓</span> Captures growth from different markets</div>
                            <div className="flex items-start gap-2"><span>✓</span> Protects against sector-specific downturns</div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>

                {/* AI Debate Tab - Scout/Skeptic/Orchestrator */}
                <TabsContent value="debate" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Scout */}
                    <Card className="border-green-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                            🔭
                          </div>
                          <span className="text-green-400">The Scout</span>
                        </CardTitle>
                        <CardDescription className="text-xs">Growth Seeker</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Conviction</span>
                          <div className="flex items-center gap-2">
                            <Progress 
                              value={rebalanceSuggestion.agent_debate?.scout?.conviction || 50} 
                              className="w-16 h-2" 
                            />
                            <span className="text-xs font-bold text-green-400">
                              {rebalanceSuggestion.agent_debate?.scout?.conviction || 50}%
                            </span>
                          </div>
                        </div>
                        <div className="p-2 bg-green-500/10 rounded-md">
                          <p className="text-xs italic">
                            "{rebalanceSuggestion.agent_debate?.scout?.growth_thesis || 'Analyzing opportunities...'}"
                          </p>
                        </div>
                        {rebalanceSuggestion.agent_debate?.scout?.key_opportunities?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium mb-1">Key Opportunities:</p>
                            <div className="flex flex-wrap gap-1">
                              {rebalanceSuggestion.agent_debate?.scout?.key_opportunities?.slice(0, 3).map((opp, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] border-green-500/50 text-green-400">
                                  {opp}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Skeptic */}
                    <Card className="border-red-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                            🛡️
                          </div>
                          <span className="text-red-400">The Skeptic</span>
                        </CardTitle>
                        <CardDescription className="text-xs">Risk Guardian</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Risk Level</span>
                          <Badge className={
                            rebalanceSuggestion.agent_debate?.skeptic?.risk_assessment === 'HIGH' ? 'bg-red-500' :
                            rebalanceSuggestion.agent_debate?.skeptic?.risk_assessment === 'MEDIUM' ? 'bg-amber-500' :
                            'bg-green-500'
                          }>
                            {rebalanceSuggestion.agent_debate?.skeptic?.risk_assessment || 'MEDIUM'}
                          </Badge>
                        </div>
                        <div className="p-2 bg-red-500/10 rounded-md">
                          <p className="text-xs italic">
                            "{rebalanceSuggestion.agent_debate?.skeptic?.risk_adjusted_view || 'Evaluating risks...'}"
                          </p>
                        </div>
                        {rebalanceSuggestion.agent_debate?.skeptic?.concerns?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium mb-1">Top Concerns:</p>
                            {rebalanceSuggestion.agent_debate?.skeptic?.concerns?.slice(0, 2).map((concern, i) => (
                              <div key={i} className="text-xs p-1 bg-muted/30 rounded mb-1">
                                <span className="font-medium">{concern.holding}:</span> {concern.issue}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Orchestrator */}
                    <Card className="border-amber-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                            ⚖️
                          </div>
                          <span className="text-amber-400">The Orchestrator</span>
                        </CardTitle>
                        <CardDescription className="text-xs">Final Arbiter</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Decision</span>
                          <Badge className="bg-amber-500">
                            {rebalanceSuggestion.decision || rebalanceSuggestion.agent_debate?.orchestrator?.decision || 'ANALYZING'}
                          </Badge>
                        </div>
                        <div className="space-y-1">
                          {rebalanceSuggestion.agent_debate?.orchestrator?.scout_ideas_accepted?.length > 0 && (
                            <div>
                              <p className="text-xs text-green-400">✓ Scout Ideas Accepted:</p>
                              <p className="text-xs text-muted-foreground">
                                {rebalanceSuggestion.agent_debate?.orchestrator?.scout_ideas_accepted?.join(', ')}
                              </p>
                            </div>
                          )}
                          {rebalanceSuggestion.agent_debate?.orchestrator?.skeptic_concerns_addressed?.length > 0 && (
                            <div>
                              <p className="text-xs text-red-400">✓ Risks Mitigated:</p>
                              <p className="text-xs text-muted-foreground">
                                {rebalanceSuggestion.agent_debate?.orchestrator?.skeptic_concerns_addressed?.join(', ')}
                              </p>
                            </div>
                          )}
                        </div>
                        {rebalanceSuggestion.agent_debate?.orchestrator?.expected_outcomes && (
                          <div className="p-2 bg-amber-500/10 rounded-md">
                            <p className="text-xs">
                              <span className="font-medium">Expected: </span>
                              Risk {rebalanceSuggestion.agent_debate?.orchestrator?.expected_outcomes?.risk_change || 'REDUCED'}, 
                              Income {rebalanceSuggestion.agent_debate?.orchestrator?.expected_outcomes?.income_impact || 'POSITIVE'}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Allocation Tab */}
                <TabsContent value="allocation" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Current Allocation Chart */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Current Allocation</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPie>
                              <Pie
                                data={getCurrentAllocationChartData()}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={70}
                                label={({ name, value }) => `${value.toFixed(0)}%`}
                                labelLine={false}
                              >
                                {getCurrentAllocationChartData().map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                            </RechartsPie>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* AI Analysis */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">AI Analysis</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Diversification</span>
                          <div className="flex items-center gap-2">
                            <Progress value={rebalanceSuggestion.ai_analysis?.diversification_score || 50} className="w-20 h-2" />
                            <span className="text-sm font-medium">{rebalanceSuggestion.ai_analysis?.diversification_score || 50}/100</span>
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Concentration Risk</span>
                          <Badge variant="outline" className={
                            rebalanceSuggestion.ai_analysis?.concentration_risk === 'HIGH' ? 'text-destructive border-destructive' :
                            rebalanceSuggestion.ai_analysis?.concentration_risk === 'MEDIUM' ? 'text-warning border-warning' :
                            'text-success border-success'
                          }>
                            {rebalanceSuggestion.ai_analysis?.concentration_risk || 'MEDIUM'}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Equity %</span>
                          <span className="text-sm">{rebalanceSuggestion.ai_analysis?.current_equity_pct || 80}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Bonds %</span>
                          <span className="text-sm">{rebalanceSuggestion.ai_analysis?.current_bond_pct || 15}%</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Changes Tab - with Units */}
                <TabsContent value="changes" className="space-y-4">
                  <div className="mb-4 p-3 bg-primary/10 rounded-lg">
                    <h4 className="font-medium text-sm mb-1">Specific Actions to Take on HL</h4>
                    <p className="text-xs text-muted-foreground">Execute these trades in order: sells first, then buys</p>
                  </div>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {rebalanceSuggestion.suggested_changes?.map((change, idx) => (
                      <div 
                        key={idx} 
                        className={`p-4 rounded-lg border ${
                          change.action === 'REDUCE' ? 'bg-red-500/5 border-red-500/30' :
                          change.action === 'INCREASE' || change.action === 'ADD' ? 'bg-green-500/5 border-green-500/30' :
                          'bg-muted/30 border-border'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={`text-xs ${
                                change.trade_direction === 'SELL' ? 'bg-red-500' : 'bg-green-500'
                              }`}>
                                {change.trade_direction || change.action}
                              </Badge>
                              <span className="font-medium text-sm">{change.name}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">{change.reason}</p>
                          </div>
                          <div className="text-right ml-4">
                            {/* Units to trade */}
                            {change.units_to_trade > 0 && (
                              <div className={`text-lg font-bold ${change.trade_direction === 'SELL' ? 'text-red-500' : 'text-green-500'}`}>
                                {change.trade_direction === 'SELL' ? '-' : '+'}{change.units_to_trade?.toFixed(2)} units
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              @ £{change.unit_price?.toFixed(2)}/unit
                            </div>
                            <p className={`text-sm font-bold mt-1 ${change.change_amount >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {change.change_amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(change.change_amount))}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* HL Instructions */}
                  {rebalanceSuggestion.hl_instructions?.length > 0 && (
                    <Card className="mt-4">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Step-by-Step HL Instructions</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {rebalanceSuggestion.hl_instructions.map((inst, i) => (
                            <div key={i} className="flex items-start gap-3 text-xs p-2 bg-muted/30 rounded">
                              <Badge variant="outline" className="shrink-0">{inst.step}</Badge>
                              <div>
                                <span className={`font-medium ${inst.action === 'SELL' ? 'text-red-500' : 'text-green-500'}`}>
                                  {inst.action}
                                </span>
                                <span className="text-muted-foreground"> {inst.holding}</span>
                                <p className="text-muted-foreground mt-1">{inst.instruction}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Simulation Tab - 5 Year Forecast */}
                <TabsContent value="simulation" className="space-y-4">
                  {rebalanceSuggestion.simulation && (
                    <div className="space-y-4">
                      {/* Comparison Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Current Portfolio */}
                        <Card className="border-muted">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm text-muted-foreground">Current Portfolio (5-Year)</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-xs text-muted-foreground">Median Value</span>
                              <span className="font-bold">{formatCurrency(rebalanceSuggestion.simulation.current_portfolio?.median_value_5yr)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs text-muted-foreground">Best Case (95th)</span>
                              <span className="text-green-500">{formatCurrency(rebalanceSuggestion.simulation.current_portfolio?.best_case_5yr)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs text-muted-foreground">Worst Case (5th)</span>
                              <span className="text-red-500">{formatCurrency(rebalanceSuggestion.simulation.current_portfolio?.worst_case_5yr)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs text-muted-foreground">Expected Volatility</span>
                              <span>{rebalanceSuggestion.simulation.current_portfolio?.expected_volatility}%</span>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Rebalanced Portfolio */}
                        <Card className="border-green-500/50">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm text-green-500">After Rebalancing (5-Year)</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-xs text-muted-foreground">Median Value</span>
                              <span className="font-bold text-green-500">{formatCurrency(rebalanceSuggestion.simulation.rebalanced_portfolio?.median_value_5yr)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs text-muted-foreground">Best Case (95th)</span>
                              <span className="text-green-500">{formatCurrency(rebalanceSuggestion.simulation.rebalanced_portfolio?.best_case_5yr)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs text-muted-foreground">Worst Case (5th)</span>
                              <span className="text-amber-500">{formatCurrency(rebalanceSuggestion.simulation.rebalanced_portfolio?.worst_case_5yr)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs text-muted-foreground">Expected Volatility</span>
                              <span className="text-green-500">{rebalanceSuggestion.simulation.rebalanced_portfolio?.expected_volatility}%</span>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Improvement Summary */}
                      <Card className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border-green-500/30">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            Rebalancing Benefit
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-2xl font-bold text-green-500">
                                +{rebalanceSuggestion.simulation.improvement?.diversification_gain}
                              </p>
                              <p className="text-xs text-muted-foreground">Diversification Score</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-blue-500">
                                -{rebalanceSuggestion.simulation.improvement?.volatility_reduction}%
                              </p>
                              <p className="text-xs text-muted-foreground">Volatility Reduction</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-amber-500">
                                +{formatCurrency(rebalanceSuggestion.simulation.improvement?.median_value_gain)}
                              </p>
                              <p className="text-xs text-muted-foreground">Extra Value (5yr)</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>

                {/* HL Basket Recommendations Tab */}
                <TabsContent value="basket" className="space-y-4">
                  {rebalanceSuggestion.recommended_basket && (
                    <div className="space-y-4">
                      <Card className="border-primary/50">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-lg">{rebalanceSuggestion.recommended_basket.name}</CardTitle>
                              <CardDescription>{rebalanceSuggestion.recommended_basket.description}</CardDescription>
                            </div>
                            <Badge className="bg-primary">Target Score: {rebalanceSuggestion.recommended_basket.target_diversification}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground mb-4">
                            {rebalanceSuggestion.recommended_basket.why_recommended}
                          </p>
                          <div className="space-y-2">
                            <h4 className="font-medium text-sm">Suggested HL Funds to Add:</h4>
                            {rebalanceSuggestion.recommended_basket.funds?.map((fund, i) => (
                              <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                                <div>
                                  <p className="font-medium text-sm">{fund.name}</p>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Badge variant="outline" className="text-[10px]">{fund.type}</Badge>
                                    <span>OCF: {fund.ocf}%</span>
                                    <span>Symbol: {fund.symbol}</span>
                                  </div>
                                </div>
                                <Badge>{fund.weight}%</Badge>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-amber-500/5 border-amber-500/20">
                        <CardContent className="py-4">
                          <h4 className="font-semibold text-sm mb-2">How to Use This Basket</h4>
                          <ol className="list-decimal list-inside text-xs space-y-1 text-muted-foreground">
                            <li>Log into your Hargreaves Lansdown account</li>
                            <li>Search for each fund by name or symbol</li>
                            <li>Check if the fund is available in your ISA/SIPP</li>
                            <li>Calculate the £ amount based on the suggested % weight</li>
                            <li>Execute buys gradually (don't invest all at once)</li>
                          </ol>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>

                {/* Run Simulation Tab - NEW */}
                <TabsContent value="runsim" className="space-y-4">
                  {!activeSimulation?.has_simulation ? (
                    <div className="space-y-4">
                      {/* Start Simulation Card */}
                      <Card className="border-amber-500/50 bg-gradient-to-br from-amber-500/5 to-amber-500/10">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Play className="w-5 h-5 text-amber-500" />
                            Start Rebalance Simulation
                          </CardTitle>
                          <CardDescription>
                            Paper trade your rebalanced portfolio and track daily performance vs. your current allocation
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Original Portfolio</p>
                              <p className="text-lg font-bold">{formatCurrency(rebalanceSuggestion.total_value)}</p>
                              <p className="text-xs text-muted-foreground">Score: {rebalanceSuggestion.diversification?.current_score}/100</p>
                            </div>
                            <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                              <p className="text-xs text-amber-500 mb-1">After Rebalancing</p>
                              <p className="text-lg font-bold">{formatCurrency(rebalanceSuggestion.total_value)}</p>
                              <p className="text-xs text-amber-500">Score: {rebalanceSuggestion.diversification?.score}/100</p>
                            </div>
                          </div>

                          {/* Trade Summary */}
                          <div className="p-3 bg-muted/30 rounded-lg">
                            <h4 className="font-medium text-sm mb-2">Trades to Execute (Simulated)</h4>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {rebalanceSuggestion.suggested_changes?.filter(c => c.action !== 'HOLD').map((trade, i) => (
                                <div key={i} className="flex justify-between text-xs">
                                  <span className={trade.trade_direction === 'SELL' ? 'text-red-500' : 'text-green-500'}>
                                    {trade.trade_direction} {trade.units_to_trade?.toFixed(2)} units
                                  </span>
                                  <span className="text-muted-foreground truncate ml-2">{trade.name?.slice(0, 30)}</span>
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between pt-2 mt-2 border-t text-xs">
                              <span>HL Trading Fees (est.)</span>
                              <span className="font-medium">£{((rebalanceSuggestion.suggested_changes?.filter(c => c.action !== 'HOLD').length || 0) * 11.95).toFixed(2)}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 p-3 bg-blue-500/10 rounded-lg text-xs">
                            <AlertTriangle className="w-4 h-4 text-blue-500 shrink-0" />
                            <p>This is a <strong>paper simulation</strong>. No real trades will be executed. Daily values update at 5:30 PM UK.</p>
                          </div>

                          <Button 
                            onClick={startSimulation} 
                            disabled={startingSimulation}
                            className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                            size="lg"
                          >
                            {startingSimulation ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting Simulation...</>
                            ) : (
                              <><Play className="w-4 h-4 mr-2" /> Start Simulation</>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Active Simulation Dashboard */}
                      <Card className="border-green-500/50">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                              <LineChart className="w-5 h-5 text-green-500" />
                              Simulation Running
                            </CardTitle>
                            <Badge className="bg-green-500 animate-pulse">Day {activeSimulation.summary?.days_running || 0}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Summary Cards */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-muted/30 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Original Portfolio</p>
                              <p className="text-lg font-bold">{formatCurrency(activeSimulation.summary?.current_original_value)}</p>
                              <p className={`text-sm ${activeSimulation.summary?.original_total_pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {activeSimulation.summary?.original_total_pnl >= 0 ? '+' : ''}{formatCurrency(activeSimulation.summary?.original_total_pnl)} 
                                ({activeSimulation.summary?.original_total_pnl_pct?.toFixed(2)}%)
                              </p>
                            </div>
                            <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                              <p className="text-xs text-green-500 mb-1">Rebalanced Portfolio</p>
                              <p className="text-lg font-bold text-green-500">{formatCurrency(activeSimulation.summary?.current_rebalanced_value)}</p>
                              <p className={`text-sm ${activeSimulation.summary?.rebalanced_total_pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {activeSimulation.summary?.rebalanced_total_pnl >= 0 ? '+' : ''}{formatCurrency(activeSimulation.summary?.rebalanced_total_pnl)} 
                                ({activeSimulation.summary?.rebalanced_total_pnl_pct?.toFixed(2)}%)
                              </p>
                            </div>
                          </div>

                          {/* Advantage */}
                          <div className={`p-4 rounded-lg ${activeSimulation.summary?.advantage >= 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                            <div className="text-center">
                              <p className="text-sm text-muted-foreground">Rebalancing Advantage</p>
                              <p className={`text-3xl font-bold ${activeSimulation.summary?.advantage >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {activeSimulation.summary?.advantage >= 0 ? '+' : ''}{formatCurrency(activeSimulation.summary?.advantage)}
                              </p>
                              <p className={`text-sm ${activeSimulation.summary?.advantage_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                ({activeSimulation.summary?.advantage_pct >= 0 ? '+' : ''}{activeSimulation.summary?.advantage_pct?.toFixed(2)}%)
                              </p>
                            </div>
                          </div>

                          {/* Daily Values Table */}
                          {activeSimulation.simulation?.daily_values?.length > 1 && (
                            <div className="p-3 bg-muted/30 rounded-lg">
                              <h4 className="font-medium text-sm mb-2">Daily Tracking (Last 7 days)</h4>
                              <div className="space-y-1 max-h-40 overflow-y-auto text-xs">
                                <div className="grid grid-cols-5 gap-2 font-medium text-muted-foreground pb-1 border-b">
                                  <span>Day</span>
                                  <span>Original</span>
                                  <span>Change</span>
                                  <span>Rebalanced</span>
                                  <span>Change</span>
                                </div>
                                {activeSimulation.simulation.daily_values.slice(-7).reverse().map((day, i) => (
                                  <div key={i} className="grid grid-cols-5 gap-2">
                                    <span className="text-muted-foreground">{day.day}</span>
                                    <span>{formatCurrency(day.original_value)}</span>
                                    <span className={day.original_pnl >= 0 ? 'text-green-500' : 'text-red-500'}>
                                      {day.original_pnl >= 0 ? '+' : ''}{day.original_pnl_pct?.toFixed(2)}%
                                    </span>
                                    <span className="text-green-600">{formatCurrency(day.rebalanced_value)}</span>
                                    <span className={day.rebalanced_pnl >= 0 ? 'text-green-500' : 'text-red-500'}>
                                      {day.rebalanced_pnl >= 0 ? '+' : ''}{day.rebalanced_pnl_pct?.toFixed(2)}%
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Milestones */}
                          {activeSimulation.simulation?.milestones_triggered?.length > 0 && (
                            <div className="p-3 bg-amber-500/10 rounded-lg">
                              <h4 className="font-medium text-sm mb-2">🎯 Milestones Reached</h4>
                              <div className="flex flex-wrap gap-2">
                                {activeSimulation.simulation.milestones_triggered.map((m, i) => (
                                  <Badge key={i} className={m.type === 'gain' ? 'bg-green-500' : 'bg-red-500'}>
                                    {m.threshold} on {new Date(m.date).toLocaleDateString()}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          <Button 
                            onClick={endSimulation} 
                            disabled={endingSimulation}
                            variant="outline"
                            className="w-full border-red-500 text-red-500 hover:bg-red-500/10"
                          >
                            {endingSimulation ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ending...</>
                            ) : (
                              <><Square className="w-4 h-4 mr-2" /> End Simulation & See Results</>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>

                {/* Original Allocation Tab */}
                <TabsContent value="allocation" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Income Forecast */}
                    <Card className="border-green-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Wallet className="w-4 h-4 text-green-500" />
                          Income Forecast (Annual)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="p-3 bg-muted/30 rounded-md">
                          <div className="flex justify-between mb-1">
                            <span className="text-xs text-muted-foreground">Current Dividend Yield</span>
                            <span className="text-sm">{rebalanceSuggestion.forecast?.dividend_yield?.current}%</span>
                          </div>
                          <div className="flex justify-between mb-1">
                            <span className="text-xs text-muted-foreground">Projected Dividend Yield</span>
                            <span className="text-sm text-green-500">{rebalanceSuggestion.forecast?.dividend_yield?.projected}%</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t">
                            <span className="text-xs font-medium">Annual Income</span>
                            <div className="text-right">
                              <span className="text-sm text-muted-foreground">{formatCurrency(rebalanceSuggestion.forecast?.annual_income?.current)}</span>
                              <ArrowRight className="w-3 h-3 inline mx-1" />
                              <span className="text-sm font-bold text-green-500">{formatCurrency(rebalanceSuggestion.forecast?.annual_income?.projected)}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Return Forecast */}
                    <Card className="border-amber-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-amber-500" />
                          Total Return Forecast
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="p-3 bg-muted/30 rounded-md">
                          <div className="flex justify-between mb-1">
                            <span className="text-xs text-muted-foreground">Expected Annual Return</span>
                            <span className="text-sm text-amber-500 font-bold">{rebalanceSuggestion.forecast?.total_return?.projected_annual_pct}%</span>
                          </div>
                          <div className="flex justify-between mb-1">
                            <span className="text-xs text-muted-foreground">Capital Gains</span>
                            <span className="text-sm">{rebalanceSuggestion.forecast?.total_return?.projected_capital_gain_pct}%</span>
                          </div>
                          <div className="flex justify-between mb-1">
                            <span className="text-xs text-muted-foreground">Dividends</span>
                            <span className="text-sm">{rebalanceSuggestion.forecast?.total_return?.projected_dividend_pct}%</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t">
                            <span className="text-xs font-medium">Projected Annual Value</span>
                            <span className="text-sm font-bold text-amber-500">
                              +{formatCurrency(rebalanceSuggestion.forecast?.total_return?.projected_annual_value)}
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Expected Volatility</span>
                          <span>{rebalanceSuggestion.forecast?.risk?.expected_volatility}%</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>

              {/* HL Instructions */}
              {rebalanceSuggestion.hl_instructions?.length > 0 && (
                <Card className="border-blue-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-blue-500" />
                      How to Execute on Hargreaves Lansdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {rebalanceSuggestion.hl_instructions.map((inst, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-2 bg-muted/30 rounded-md">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            inst.action === 'SELL' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                          }`}>
                            {inst.step}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{inst.holding}</p>
                            <p className="text-xs text-muted-foreground">{inst.instruction}</p>
                          </div>
                          <Badge variant="outline">{formatCurrency(inst.amount)}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
            {rebalanceSuggestion && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => window.open('https://www.hl.co.uk/my-accounts', '_blank')}
                  data-testid="open-hl-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open HL Website
                </Button>
                <Button 
                  onClick={markAsPendingRebalance}
                  disabled={markingPending}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  data-testid="mark-pending-btn"
                >
                  {markingPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <BookmarkCheck className="w-4 h-4 mr-2" />
                  )}
                  Mark as Pending Rebalance
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fund Analysis Dialog */}
      <Dialog open={fundAnalysisDialogOpen} onOpenChange={setFundAnalysisDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-cyan-500" />
              Fund Intelligence Report
            </DialogTitle>
            <DialogDescription>
              {selectedPortfolioForAnalysis?.name} • Overlap, Fee & Correlation Analysis
            </DialogDescription>
          </DialogHeader>

          {fundAnalysisLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-cyan-500 mb-4" />
              <p className="text-sm text-muted-foreground">Analyzing fund holdings, fees, and correlations...</p>
            </div>
          ) : fundAnalysis ? (
            <div className="space-y-6 py-4">
              {/* Overall Score */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg border border-cyan-500/20">
                <div>
                  <p className="text-sm text-muted-foreground">Portfolio Intelligence Score</p>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {fundAnalysis.overall_score}/100
                  </p>
                </div>
                <div className="w-24 h-24">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={[
                          { name: 'Score', value: fundAnalysis.overall_score },
                          { name: 'Remaining', value: 100 - fundAnalysis.overall_score }
                        ]}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={25}
                        outerRadius={40}
                        startAngle={90}
                        endAngle={-270}
                      >
                        <Cell fill="#06b6d4" />
                        <Cell fill="#1e293b" />
                      </Pie>
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Recommendations */}
              {fundAnalysis.recommendations?.length > 0 && (
                <Card className="border-amber-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      Key Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {fundAnalysis.recommendations.map((rec, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        rec.priority === 'HIGH' ? 'bg-red-500/10 border-red-500/30' :
                        'bg-amber-500/10 border-amber-500/30'
                      }`}>
                        <div className="flex items-start gap-2">
                          <Badge className={rec.priority === 'HIGH' ? 'bg-red-500' : 'bg-amber-500'}>
                            {rec.type}
                          </Badge>
                          <div>
                            <p className="text-sm font-medium">{rec.message}</p>
                            {rec.details?.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {rec.details.slice(0, 2).join(' • ')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Tabs */}
              <Tabs defaultValue="fees" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="fees">💷 Fee Analysis</TabsTrigger>
                  <TabsTrigger value="overlap">🔗 Holdings Overlap</TabsTrigger>
                  <TabsTrigger value="correlation">📊 Correlation</TabsTrigger>
                </TabsList>

                {/* Fee Analysis Tab */}
                <TabsContent value="fees" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="border-green-500/30">
                      <CardContent className="pt-4 text-center">
                        <DollarSign className="w-6 h-6 mx-auto mb-2 text-green-500" />
                        <p className="text-2xl font-bold text-green-500">
                          {formatCurrency(fundAnalysis.fee_analysis?.total_annual_fees || 0)}
                        </p>
                        <p className="text-xs text-muted-foreground">Annual Fees</p>
                      </CardContent>
                    </Card>
                    <Card className="border-blue-500/30">
                      <CardContent className="pt-4 text-center">
                        <ArrowRight className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                        <p className="text-2xl font-bold text-blue-500">
                          {formatCurrency(fundAnalysis.fee_analysis?.total_passive_equivalent || 0)}
                        </p>
                        <p className="text-xs text-muted-foreground">Passive Equivalent</p>
                      </CardContent>
                    </Card>
                    <Card className="border-amber-500/30">
                      <CardContent className="pt-4 text-center">
                        <Wallet className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                        <p className="text-2xl font-bold text-amber-500">
                          {formatCurrency(fundAnalysis.fee_analysis?.potential_annual_savings || 0)}
                        </p>
                        <p className="text-xs text-muted-foreground">Potential Savings/yr</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Fee Breakdown by Fund</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {fundAnalysis.fee_analysis?.fund_breakdown?.map((fund, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{fund.name}</p>
                              <p className="text-xs text-muted-foreground">
                                OCF: {fund.ocf}% • Value: {formatCurrency(fund.value)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium">{formatCurrency(fund.annual_fee)}/yr</p>
                              {fund.passive_alternative && (
                                <p className="text-xs text-green-500">
                                  Save {formatCurrency(fund.potential_savings)} with {fund.passive_alternative}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20 text-center">
                    <p className="text-sm">
                      <span className="font-medium">10-Year Fee Drag:</span>{" "}
                      <span className="text-cyan-400 font-bold">
                        {formatCurrency(fundAnalysis.fee_analysis?.fee_drag_10yr || 0)}
                      </span>
                      {" "}lost to fees vs passive alternatives
                    </p>
                  </div>
                </TabsContent>

                {/* Holdings Overlap Tab */}
                <TabsContent value="overlap" className="space-y-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Concentrated Holdings
                      </CardTitle>
                      <CardDescription>
                        Top underlying holdings across all your funds
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {fundAnalysis.overlap_analysis?.concentrated_holdings?.length > 0 ? (
                        <div className="space-y-2">
                          {fundAnalysis.overlap_analysis.concentrated_holdings.slice(0, 8).map((holding, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="w-8 text-center">
                                <span className="text-xs font-bold text-muted-foreground">#{idx + 1}</span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium">{holding.name}</span>
                                  <span className="text-sm font-bold">
                                    {holding.total_weight?.toFixed(1)}%
                                  </span>
                                </div>
                                <Progress 
                                  value={Math.min(100, holding.total_weight * 5)} 
                                  className="h-1.5" 
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No significant concentration detected
                        </p>
                      )}
                    </CardContent>
                  </Card>
                  
                  {fundAnalysis.overlap_analysis?.warnings?.length > 0 && (
                    <Card className="border-red-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-red-400">⚠️ Concentration Warnings</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {fundAnalysis.overlap_analysis.warnings.map((warning, idx) => (
                            <p key={idx} className="text-sm p-2 bg-red-500/10 rounded-md">
                              {warning}
                            </p>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Correlation Tab */}
                <TabsContent value="correlation" className="space-y-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <CircleDot className="w-4 h-4" />
                        Fund Correlation Matrix
                      </CardTitle>
                      <CardDescription>
                        How your funds move together (1.0 = identical, 0 = independent)
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {fundAnalysis.correlation_analysis?.matrix?.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr>
                                <th className="p-2"></th>
                                {fundAnalysis.correlation_analysis.labels?.map((label, idx) => (
                                  <th key={idx} className="p-2 font-medium text-left max-w-[100px] truncate">
                                    {label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {fundAnalysis.correlation_analysis.matrix?.map((row, i) => (
                                <tr key={i}>
                                  <td className="p-2 font-medium max-w-[100px] truncate">
                                    {fundAnalysis.correlation_analysis.labels?.[i]}
                                  </td>
                                  {row.map((cell, j) => (
                                    <td key={j} className="p-1">
                                      <div className={`p-2 rounded text-center ${
                                        cell.value === 1 ? 'bg-muted/50' :
                                        cell.high_correlation ? 'bg-red-500/30 text-red-400 font-bold' :
                                        cell.value > 0.5 ? 'bg-amber-500/20' :
                                        'bg-green-500/20'
                                      }`}>
                                        {cell.value?.toFixed(2)}
                                      </div>
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Need at least 2 funds with price data to calculate correlation
                        </p>
                      )}
                      
                      {fundAnalysis.correlation_analysis?.average_correlation > 0 && (
                        <div className="mt-4 p-3 bg-muted/30 rounded-md text-center">
                          <p className="text-sm">
                            Average Portfolio Correlation:{" "}
                            <span className={`font-bold ${
                              fundAnalysis.correlation_analysis.average_correlation > 0.7 ? 'text-red-400' :
                              fundAnalysis.correlation_analysis.average_correlation > 0.5 ? 'text-amber-400' :
                              'text-green-400'
                            }`}>
                              {fundAnalysis.correlation_analysis.average_correlation?.toFixed(2)}
                            </span>
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {fundAnalysis.correlation_analysis?.clone_warnings?.length > 0 && (
                    <Card className="border-red-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-red-400">🔴 Clone Fund Warnings</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {fundAnalysis.correlation_analysis.clone_warnings.map((warning, idx) => (
                            <div key={idx} className="p-3 bg-red-500/10 rounded-md">
                              <p className="text-sm font-medium">
                                {warning.fund1} ↔ {warning.fund2}
                              </p>
                              <p className="text-xs text-muted-foreground">{warning.warning}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : null}

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
            <Button 
              onClick={() => {
                setFundAnalysisDialogOpen(false);
                openRebalanceDialog(selectedPortfolioForAnalysis);
              }}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              <Scale className="w-4 h-4 mr-2" />
              Rebalance Portfolio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fund Deep Dive Modal */}
      <FundDeepDive
        fundName={selectedFundForDeepDive}
        isOpen={fundDeepDiveOpen}
        onClose={() => {
          setFundDeepDiveOpen(false);
          setSelectedFundForDeepDive(null);
        }}
      />

      {/* AI Rebalance Wizard Modal */}
      <RebalanceWizard
        portfolioId={selectedPortfolioForWizard?.id}
        portfolioName={selectedPortfolioForWizard?.name}
        isOpen={rebalanceWizardOpen}
        onClose={() => {
          setRebalanceWizardOpen(false);
          setSelectedPortfolioForWizard(null);
        }}
        onComplete={() => {
          fetchPortfolios();
          toast.success("Rebalance complete! Refresh to see updated holdings.");
        }}
      />

      {/* AI Invest Plan Modal */}
      <AIInvestPlanDialog
        portfolio={selectedPortfolioForAiPlan}
        isOpen={aiInvestPlanOpen}
        onClose={() => {
          setAiInvestPlanOpen(false);
          setSelectedPortfolioForAiPlan(null);
        }}
      />

      {/* Monthly Top-Up Modal */}
      <MonthlyTopUpDialog
        portfolio={selectedPortfolioForTopup}
        isOpen={monthlyTopupOpen}
        onClose={() => {
          setMonthlyTopupOpen(false);
          setSelectedPortfolioForTopup(null);
        }}
        onUpdated={fetchPortfolios}
      />
    </div>
  );
};

export default MyPortfolios;
